import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchEventNotifications } from "../api/fetchEventNotifications/fetchEventNotificationsApi";
import { patchEventNotification } from "../api/patchEventNotification/patchEventNotificationApi";
import { EventNotification } from "../api/fetchEventNotifications/types";
import { AppDispatch, AppThunk } from "@/app/store";

interface EventNotificationsState {
  notifications: EventNotification[];
  totalCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: EventNotificationsState = {
  notifications: [],
  totalCount: 0,
  loading: false,
  error: null,
};

const eventNotificationsSlice = createSlice({
  name: "eventNotifications",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setNotifications: (
      state,
      action: PayloadAction<{
        notifications: EventNotification[];
        totalCount: number;
      }>,
    ) => {
      state.notifications = action.payload.notifications;
      state.totalCount = action.payload.totalCount;
    },
    updateNotification: (state, action: PayloadAction<EventNotification>) => {
      const index = state.notifications.findIndex(
        (n) => n.id === action.payload.id,
      );
      if (index !== -1) {
        state.notifications[index] = action.payload;
      }
    },
  },
});

export const { setLoading, setError, setNotifications, updateNotification } =
  eventNotificationsSlice.actions;

export const fetchEventNotificationsAction =
  (): AppThunk => async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response = await fetchEventNotifications();
      dispatch(
        setNotifications({
          notifications: response.data,
          totalCount: response.meta?.totalCount ?? 0,
        }),
      );
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch event notifications"));
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

export const toggleNotificationChannelAction =
  (
    notification: EventNotification,
    channel: "email" | "sms",
    enabled: boolean,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    const patchData =
      channel === "email"
        ? { isMailChannelEnabled: enabled }
        : { isSmsChannelEnabled: enabled };

    try {
      const response = await patchEventNotification(notification.id, patchData);
      dispatch(updateNotification(response.data));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to update notification"));
      }
    }
  };

export default eventNotificationsSlice.reducer;
